import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import Button from 'web-library/stories/atom/Button/Button';
import Dialog from 'web-library/stories/atom/Dialog/Dialog';
import DialogTitle from 'web-library/stories/molecule/DialogTitle/DialogTitle';
import DialogContent from 'web-library/stories/molecule/DialogContent/DialogContent';
import DialogActions from 'web-library/stories/molecule/DialogActions/DialogActions';
import { usePostLogout } from '../../../api-refactored/user/usePostLogout';
import { useCountDown } from '../../../hooks/timer';
import { GET_USER_DETAILS_QUERY_KEY } from '../../../constants/queryKeys';

const useStyles = makeStyles()((theme) =>
({
  root: {
    margin: theme.spacing(2),
  },
}));

interface InactivitySessionProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const InactivitySession = ({ open, setOpen }: InactivitySessionProps) => {
  const { classes } = useStyles();
  const { mutate: logout } = usePostLogout({
    refreshPage: false,
    redirectToHome: true,
    inactive: true,
  });
  const queryClient = useQueryClient();
  const { countDown, countDownEnded, reset } = useCountDown(0);
  const [oneMinCountdown, setOneMinCountdown] = useState(false);
  const { t } = useTranslation();
  const intercepterKey = 'sessionInactivityIntercepted';

  const setSessionExpiryIntercepted = useCallback(() => {
    const now = new Date().getTime();
    const obj = { value: true, time: now };
    localStorage.setItem(intercepterKey, JSON.stringify(obj));
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setOneMinCountdown(false);
    reset(0);
  }, [reset, setOpen]);

  const handleContinue = useCallback(() => {
    setSessionExpiryIntercepted();
    queryClient.invalidateQueries(GET_USER_DETAILS_QUERY_KEY);
    handleClose();
  }, [handleClose, queryClient, setSessionExpiryIntercepted]);

  const handleLogout = useCallback(() => {
    setSessionExpiryIntercepted();
    logout();
    handleClose();
  }, [handleClose, logout, setSessionExpiryIntercepted]);

  useEffect(() => {
    if (open) {
      reset(60 * 1000);
      setOneMinCountdown(true);
    }
  }, [open, reset]);

  useEffect(() => {
    if (countDownEnded && oneMinCountdown) {
      handleLogout();
    }
  }, [countDownEnded, handleLogout, oneMinCountdown]);

  useEffect(() => {
    const handleInterceptEvent = (e: any) => {
      if (e.key === intercepterKey) {
        const intercept = localStorage.getItem(intercepterKey);
        if (intercept) {
          queryClient.invalidateQueries(GET_USER_DETAILS_QUERY_KEY);
          handleClose();
        }
      }
    };
    window.addEventListener('storage', handleInterceptEvent);
    return () => window.removeEventListener('storage', handleInterceptEvent);
  }, [handleClose, queryClient]);

  return (
    <Dialog
      className={classes.root}
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
    >
      <DialogTitle>{t('session->>InactivitySession.Session Timeout')}</DialogTitle>
      <DialogContent>
        {t('session->>InactivitySession.YourOnlineSessionExpire', {
          countdown: countDown.format('mm:ss'),
        })}
      </DialogContent>
      <DialogActions>
        <Button type="button" variant="text" color="primary" onClick={handleContinue}>
          {t('session->>InactivitySession. Continue')}
        </Button>
        <Button type="button" variant="text" color="primary" onClick={handleLogout}>
          {t('session->>InactivitySession. Logout')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InactivitySession;
